// filepath: packages/ui/src/hooks/ToastProvider.tsx
import { createContext, useContext, ReactNode, useMemo } from 'react';
import type { UseToastReturn } from './useToast';
import { useToast as useToastImpl } from './useToast';

// Separate contexts for state and actions to prevent unnecessary re-renders
const ToastStateContext = createContext<{ toasts: UseToastReturn['toasts'] } | undefined>(undefined);
const ToastActionsContext = createContext<Omit<UseToastReturn, 'toasts'> | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  // Create a single shared toast instance for the subtree
  const toastApi = useToastImpl();
  
  // Split state and actions into separate contexts
  const state = useMemo(() => ({ toasts: toastApi.toasts }), [toastApi.toasts]);
  const actions = useMemo(() => ({
    toast: toastApi.toast,
    success: toastApi.success,
    error: toastApi.error,
    warning: toastApi.warning,
    info: toastApi.info,
    dismiss: toastApi.dismiss,
    dismissAll: toastApi.dismissAll,
  }), [toastApi.toast, toastApi.success, toastApi.error, toastApi.warning, toastApi.info, toastApi.dismiss, toastApi.dismissAll]);
  
  return (
    <ToastStateContext.Provider value={state}>
      <ToastActionsContext.Provider value={actions}>
        {children}
      </ToastActionsContext.Provider>
    </ToastStateContext.Provider>
  );
}

// Hook to get toast actions only (doesn't cause re-renders when toasts change)
export function useToastActions(): Omit<UseToastReturn, 'toasts'> {
  const ctx = useContext(ToastActionsContext);
  if (!ctx) {
    // Fallback: create an isolated instance if provider not found
    const fallback = useToastImpl();
    return {
      toast: fallback.toast,
      success: fallback.success,
      error: fallback.error,
      warning: fallback.warning,
      info: fallback.info,
      dismiss: fallback.dismiss,
      dismissAll: fallback.dismissAll,
    };
  }
  return ctx;
}

// Hook to get full toast context (includes toasts state - will cause re-renders)
export function useToastContext(): UseToastReturn {
  const state = useContext(ToastStateContext);
  const actions = useContext(ToastActionsContext);
  
  if (!state || !actions) {
    // Fallback: create an isolated instance if provider not found
    return useToastImpl();
  }
  
  return {
    toasts: state.toasts,
    ...actions,
  };
}
